import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Edit3, Check, X, Printer, RefreshCw, CheckCircle, Archive,
  Loader2, AlertTriangle, Save,
  Database, FileText as FileTextIcon, Sparkles, Target,
  TrendingUp, AlertCircle,
  Beef, Weight, HeartPulse, DollarSign, Leaf, ClipboardList, CloudRain,
  Droplets, Activity, Calendar, PieChart as PieChartIcon, BarChart3, Scissors,
  Smartphone, ChevronDown, ChevronUp
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  ScatterChart, Scatter, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import {
  getReport, updateReportSection, approveReport, archiveReport, regenerateReport
} from '../../services/api'
import clsx from 'clsx'

// Design palette from the report spec
const PALETTE = {
  primary: '#1B4332',
  positive: '#52796F',
  warning: '#D4A017',
  negative: '#A63D40',
  neutral: '#6C757D',
  accent: '#B8860B',
}

// Ecological section palette
const ECO_PALETTE = {
  healthy: '#2D6A4F',
  good_recovery: '#52B788',
  moderate: '#B5C99A',
  stressed: '#D4A017',
  degraded: '#9C6644',
  critical: '#A63D40',
  lantana: '#6A0572',
  water: '#4A7C9B',
  grazing: '#E07A2F',
  rainfall: '#7FB3D8',
}

const CHART_COLORS = ['#1B4332', '#52796F', '#D4A017', '#A63D40', '#6C757D', '#B8860B', '#8FBC8F', '#CD853F']

// Dairy chart palette (sky blue theme)
const DAIRY_CHART_COLORS = ['#2563EB', '#0EA5E9', '#10B981', '#EC4899', '#FBBF24', '#34D399', '#3B82F6', '#DC2626']

const STATUS_STYLES = {
  green: { border: 'border-l-green-500', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500', ring: 'ring-green-200' },
  amber: { border: 'border-l-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', ring: 'ring-amber-200' },
  red:   { border: 'border-l-red-500',   bg: 'bg-red-50',   text: 'text-red-700',   dot: 'bg-red-500',   ring: 'ring-red-200' },
}

const ICON_MAP = {
  cow: Beef,
  weight: Weight,
  health: HeartPulse,
  money: DollarSign,
  leaf: Leaf,
  clipboard: ClipboardList,
  rain: CloudRain,
  // Dairy icons
  milk: Droplets,
  activity: Activity,
  calendar: Calendar,
  'pie-chart': PieChartIcon,
  'dollar-sign': DollarSign,
  'bar-chart': BarChart3,
  'trending-up': TrendingUp,
  'clipboard-check': ClipboardList,
  layers: Leaf,
  scissors: Scissors,
  smartphone: Smartphone,
}

// =============================================================================
// Shared Tooltip Styling
// =============================================================================

const CustomTooltipWrapper = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white rounded-lg shadow-lg border border-earth-200 px-4 py-3 text-sm">
      {label && <p className="font-semibold text-earth-800 mb-1.5 border-b border-earth-100 pb-1.5">{label}</p>}
      <div className="space-y-1">
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color || entry.fill }} />
            <span className="text-earth-600">{entry.name}:</span>
            <span className="font-semibold text-earth-900 ml-auto">
              {formatter ? formatter(entry.value, entry.name) : (typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Format large numbers nicely (e.g. 18,195,000 → 18.2M)
const formatCompactNumber = (val) => {
  if (typeof val !== 'number') return val
  if (Math.abs(val) >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`
  if (Math.abs(val) >= 10_000) return `${(val / 1_000).toFixed(0)}K`
  if (Math.abs(val) >= 1_000) return `${(val / 1_000).toFixed(1)}K`
  return val.toLocaleString()
}

// =============================================================================
// KPI Tile Row — traffic-light cards at top of report
// =============================================================================

function KPITileRow({ tiles }) {
  if (!tiles || tiles.length === 0) return null

  // Adaptive grid: 6 cols for ≤6 tiles, 5 cols for 7-10 tiles
  const gridCols = tiles.length <= 6 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'

  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 ${gridCols} gap-3 mb-6`}>
      {tiles.map((tile, i) => {
        const style = STATUS_STYLES[tile.status] || STATUS_STYLES.green
        const Icon = ICON_MAP[tile.icon_hint] || ClipboardList

        return (
          <div
            key={i}
            className={clsx(
              'rounded-lg border-l-4 p-3.5 shadow-sm transition-shadow hover:shadow-md',
              style.border, 'bg-white'
            )}
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className={clsx('p-1 rounded', style.bg)}>
                <Icon className={clsx('h-3.5 w-3.5', style.text)} />
              </div>
              <span className="text-xs font-medium text-earth-500 uppercase tracking-wide truncate">{tile.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 leading-tight tracking-tight">{tile.value}</p>
            {tile.subtitle && (
              <p className="text-xs text-earth-600 mt-1 leading-snug">{tile.subtitle}</p>
            )}
            {tile.context && (
              <p className="text-[10px] text-earth-400 mt-1 leading-tight">{tile.context}</p>
            )}
          </div>
        )
      })}
    </div>
  )
}

// =============================================================================
// Anomaly Alert Box — red/amber alerts below KPIs
// =============================================================================

function AnomalyAlertBox({ alerts }) {
  if (!alerts || alerts.length === 0) return null

  const reds = alerts.filter(a => a.severity === 'red')
  const ambers = alerts.filter(a => a.severity === 'amber')
  const sorted = [...reds, ...ambers]

  return (
    <div className="rounded-xl border border-red-200 bg-white shadow-sm p-5 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-1.5 rounded-lg bg-red-100">
          <AlertCircle className="h-4 w-4 text-red-600" />
        </div>
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
          Anomaly Alerts
        </h3>
        <span className="ml-auto text-xs font-medium text-earth-400">
          {reds.length > 0 && <span className="text-red-600">{reds.length} critical</span>}
          {reds.length > 0 && ambers.length > 0 && ' · '}
          {ambers.length > 0 && <span className="text-amber-600">{ambers.length} warning</span>}
        </span>
      </div>
      <div className="space-y-2.5">
        {sorted.map((alert, i) => {
          const isRed = alert.severity === 'red'
          return (
            <div key={i} className={clsx(
              'flex items-start gap-3 p-3 rounded-lg',
              isRed ? 'bg-red-50' : 'bg-amber-50'
            )}>
              <span className={clsx(
                'mt-0.5 h-2.5 w-2.5 rounded-full shrink-0',
                isRed ? 'bg-red-500' : 'bg-amber-500'
              )} />
              <div className="min-w-0 flex-1">
                <span className={clsx(
                  'text-sm font-semibold',
                  isRed ? 'text-red-800' : 'text-amber-800'
                )}>
                  {alert.title}
                </span>
                {alert.text && (
                  <p className="text-sm text-earth-600 mt-0.5 leading-relaxed">{alert.text}</p>
                )}
              </div>
              {alert.section_ref && (
                <a
                  href={`#section-${alert.section_ref}`}
                  className={clsx(
                    'shrink-0 text-xs font-medium px-2 py-1 rounded-full',
                    isRed ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  )}
                >
                  {alert.section_ref}
                </a>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// Report Table — generic structured table for sections
// =============================================================================

// Long tables collapse to the first rows by default; print always shows all rows
const TABLE_COLLAPSE_THRESHOLD = 12
const TABLE_COLLAPSED_ROWS = 10

function ReportTable({ table }) {
  const { title, headers, rows } = table
  const [expanded, setExpanded] = useState(false)
  if (!headers || !rows || rows.length === 0) return null

  const collapsible = rows.length > TABLE_COLLAPSE_THRESHOLD

  const isNumeric = (val) => typeof val === 'number' || (typeof val === 'string' && /^-?[\d,.]+%?$/.test(val.trim()))
  const isLastRow = (ri) => ri === rows.length - 1
  const isTotalRow = (row) => {
    const first = Array.isArray(row) ? row[0] : Object.values(row)[0]
    return typeof first === 'string' && /^total/i.test(first.trim())
  }

  return (
    <div className="my-5 overflow-x-auto rounded-lg border border-earth-200">
      {title && (
        <div className="px-4 py-2.5 bg-earth-50 border-b border-earth-200">
          <h5 className="text-sm font-semibold text-earth-700">{title}</h5>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-earth-200 bg-earth-50/50">
            {headers.map((h, i) => (
              <th
                key={i}
                className={clsx(
                  'px-4 py-2.5 font-semibold text-earth-600 text-xs uppercase tracking-wider',
                  i === 0 ? 'text-left' : 'text-right'
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-earth-100">
          {rows.map((row, ri) => {
            const cells = Array.isArray(row) ? row : Object.values(row)
            const isTotal = isTotalRow(row)
            const hiddenWhenCollapsed = collapsible && !expanded && ri >= TABLE_COLLAPSED_ROWS
            return (
              <tr key={ri} className={clsx(
                isTotal && 'bg-earth-50 font-semibold border-t-2 border-earth-300',
                !isTotal && ri % 2 === 1 && 'bg-earth-50/30',
                hiddenWhenCollapsed && 'hidden print:table-row'
              )}>
                {cells.map((cell, ci) => (
                  <td
                    key={ci}
                    className={clsx(
                      'px-4 py-2',
                      ci === 0 ? 'text-left text-earth-800' : 'text-right text-earth-600',
                      isTotal && 'text-earth-900',
                      isNumeric(cell) && ci > 0 && 'tabular-nums font-mono text-xs'
                    )}
                  >
                    {cell != null ? (typeof cell === 'number' ? cell.toLocaleString() : String(cell)) : '—'}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold text-primary-700 bg-earth-50 border-t border-earth-200 hover:bg-earth-100 transition-colors print:hidden"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" />
              Show fewer
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" />
              Show all {rows.length} rows
            </>
          )}
        </button>
      )}
    </div>
  )
}

// =============================================================================
// Custom Pie Label — clean percentage labels outside the donut
// =============================================================================

const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value, percent }) => {
  if (percent < 0.03) return null // hide tiny slices
  const RADIAN = Math.PI / 180
  const radius = outerRadius + 24
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {name} ({(percent * 100).toFixed(0)}%)
    </text>
  )
}

// =============================================================================
// Custom Legend — cleaner than Recharts default
// =============================================================================

const CustomLegend = ({ payload }) => {
  if (!payload?.length) return null
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-3">
      {payload.map((entry, i) => {
        const color = typeof entry.color === 'string' ? entry.color : '#6B7280'
        const label = typeof entry.value === 'string' ? entry.value
          : typeof entry.value === 'object' && entry.value !== null ? (entry.value.name || entry.value.dataKey || '')
          : (entry.dataKey || entry.name || String(entry.value ?? ''))
        return (
          <div key={i} className="flex items-center gap-1.5 text-xs text-earth-600">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />
            <span>{label}</span>
          </div>
        )
      })}
    </div>
  )
}

// =============================================================================
// Report Chart — supports bar, line, pie, area, scatter, composed
// =============================================================================

// Uniform-sample an array down to `cap` items. Keeps first + last + evenly
// spaced interior picks so line/area chart shape is preserved. Bar/pie charts
// intentionally skip this (downsampling categorical bars loses semantics).
const MAX_CHART_POINTS = 2000
function downsampleForChart(data, chartType) {
  if (!data || data.length <= MAX_CHART_POINTS) return data
  const streamingTypes = new Set(['line', 'area', 'scatter', 'composed'])
  if (!streamingTypes.has(chartType)) return data
  const step = data.length / MAX_CHART_POINTS
  const out = new Array(MAX_CHART_POINTS)
  for (let i = 0; i < MAX_CHART_POINTS; i++) {
    out[i] = data[Math.floor(i * step)]
  }
  out[MAX_CHART_POINTS - 1] = data[data.length - 1]
  return out
}

function ReportChart({ chart }) {
  const { chart_type, title, config = {} } = chart
  const rawData = chart.data
  const data = downsampleForChart(rawData, chart_type)
  const colors = config.colors || CHART_COLORS
  const dataKey = config.dataKey || 'value'
  const xAxisKey = config.xAxisKey || 'name'

  if (!data || data.length === 0) return null

  // Detect if values are large (UGX) for compact formatting
  const maxVal = Math.max(...data.map(d => {
    const vals = Object.values(d).filter(v => typeof v === 'number')
    return vals.length ? Math.max(...vals) : 0
  }))
  const tickFormatter = maxVal >= 10_000 ? formatCompactNumber : (v) => (typeof v === 'number' ? v.toLocaleString() : v)
  const tooltipFormatter = (v, name) => typeof v === 'number' ? v.toLocaleString() : v

  const axisStyle = { fontSize: 11, fill: '#6B7280' }
  const gridProps = { strokeDasharray: '3 3', stroke: '#E5E7EB', strokeOpacity: 0.7 }

  const renderChart = () => {
    switch (chart_type) {
      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={90}
              paddingAngle={2}
              dataKey={dataKey}
              nameKey={xAxisKey}
              label={renderPieLabel}
              labelLine={{ stroke: '#9CA3AF', strokeWidth: 1 }}
              strokeWidth={2}
              stroke="#fff"
            >
              {data.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltipWrapper formatter={tooltipFormatter} />}
            />
          </PieChart>
        )

      case 'line':
        return (
          <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey={xAxisKey} tick={axisStyle} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <YAxis tick={axisStyle} tickFormatter={tickFormatter} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltipWrapper formatter={tooltipFormatter} />} />
            <Legend content={<CustomLegend />} />
            {Object.keys(data[0] || {}).filter(k => k !== xAxisKey).map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[i % colors.length]}
                strokeWidth={2.5}
                dot={{ r: 4, fill: '#fff', stroke: colors[i % colors.length], strokeWidth: 2 }}
                activeDot={{ r: 6, fill: colors[i % colors.length] }}
              />
            ))}
          </LineChart>
        )

      case 'area':
        return (
          <AreaChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey={xAxisKey} tick={axisStyle} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <YAxis tick={axisStyle} tickFormatter={tickFormatter} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltipWrapper formatter={tooltipFormatter} />} />
            <Legend content={<CustomLegend />} />
            {Object.keys(data[0] || {}).filter(k => k !== xAxisKey).map((key, i) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                fill={colors[i % colors.length]}
                fillOpacity={0.15}
                stroke={colors[i % colors.length]}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        )

      case 'scatter': {
        const xDataKey = config.xDataKey || 'x'
        const yDataKey = config.yDataKey || 'y'
        return (
          <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
            <CartesianGrid {...gridProps} />
            <XAxis
              type="number"
              dataKey={xDataKey}
              name={config.xAxisLabel || 'X'}
              tick={axisStyle}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
              label={config.xAxisLabel ? { value: config.xAxisLabel, position: 'insideBottom', offset: -12, style: { fontSize: 11, fill: '#6B7280' } } : undefined}
            />
            <YAxis
              type="number"
              dataKey={yDataKey}
              name={config.yAxisLabel || 'Y'}
              tick={axisStyle}
              axisLine={false}
              tickLine={false}
              label={config.yAxisLabel ? { value: config.yAxisLabel, angle: -90, position: 'insideLeft', offset: 5, style: { fontSize: 11, fill: '#6B7280' } } : undefined}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3', stroke: '#9CA3AF' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const d = payload[0]?.payload
                return (
                  <div className="bg-white rounded-lg shadow-lg border border-earth-200 px-4 py-3 text-sm">
                    {d?.name && <p className="font-semibold text-earth-800 mb-1 border-b border-earth-100 pb-1">{d.name}</p>}
                    <div className="space-y-0.5 text-earth-600">
                      <p>{config.xAxisLabel || 'X'}: <span className="font-semibold text-earth-900">{d?.[xDataKey]?.toLocaleString()}</span></p>
                      <p>{config.yAxisLabel || 'Y'}: <span className="font-semibold text-earth-900">{d?.[yDataKey]?.toLocaleString()}</span></p>
                    </div>
                  </div>
                )
              }}
            />
            <Scatter data={data} strokeWidth={1.5} stroke="#fff">
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.color || colors[0]} r={6} />
              ))}
            </Scatter>
          </ScatterChart>
        )
      }

      case 'composed':
        return (
          <ComposedChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey={xAxisKey} tick={axisStyle} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <YAxis
              yAxisId="left"
              tick={axisStyle}
              tickFormatter={tickFormatter}
              axisLine={false}
              tickLine={false}
              label={config.yAxisLeft ? { value: config.yAxisLeft, angle: -90, position: 'insideLeft', offset: 0, style: { fontSize: 11, fill: '#6B7280' } } : undefined}
            />
            {config.yAxisRight && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={axisStyle}
                tickFormatter={tickFormatter}
                axisLine={false}
                tickLine={false}
                label={{ value: config.yAxisRight, angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#6B7280' } }}
              />
            )}
            <Tooltip content={<CustomTooltipWrapper formatter={tooltipFormatter} />} />
            <Legend content={<CustomLegend />} />
            {(config.bars || []).map((barKey, i) => (
              <Bar
                key={barKey}
                yAxisId={config.barAxis || 'left'}
                dataKey={barKey}
                fill={colors[i % colors.length]}
                radius={[6, 6, 0, 0]}
                maxBarSize={60}
              />
            ))}
            {(config.lines || []).map((lineKey, i) => (
              <Line
                key={lineKey}
                yAxisId={config.lineAxis || (config.yAxisRight ? 'right' : 'left')}
                type="monotone"
                dataKey={lineKey}
                stroke={colors[(i + (config.bars || []).length) % colors.length]}
                strokeWidth={2.5}
                dot={{ r: 4, fill: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </ComposedChart>
        )

      default: // bar
        return (
          <BarChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey={xAxisKey} tick={axisStyle} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <YAxis tick={axisStyle} tickFormatter={tickFormatter} axisLine={false} tickLine={false}
              label={config.yAxisLeft ? { value: config.yAxisLeft, angle: -90, position: 'insideLeft', offset: 0, style: { fontSize: 11, fill: '#6B7280' } } : undefined}
            />
            <Tooltip content={<CustomTooltipWrapper formatter={tooltipFormatter} />} />
            <Legend content={<CustomLegend />} />
            {Object.keys(data[0] || {}).filter(k => k !== xAxisKey).map((key, i, arr) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colors[i % colors.length]}
                radius={config.stacked && i < arr.length - 1 ? [0, 0, 0, 0] : [6, 6, 0, 0]}
                maxBarSize={60}
                {...(config.stacked ? { stackId: 'stack' } : {})}
              />
            ))}
          </BarChart>
        )
    }
  }

  return (
    <div className="bg-white rounded-lg border border-earth-200 shadow-sm p-5 my-4">
      {title && <h4 className="text-sm font-semibold text-earth-700 mb-4">{title}</h4>}
      <ResponsiveContainer width="100%" height={chart_type === 'pie' ? 300 : 280}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  )
}

// =============================================================================
// Recommendation Card
// =============================================================================

const PRIORITY_STYLES = {
  high:   { border: 'border-l-red-500',   bg: 'bg-white', badge: 'bg-red-100 text-red-700' },
  medium: { border: 'border-l-amber-500', bg: 'bg-white', badge: 'bg-amber-100 text-amber-700' },
  low:    { border: 'border-l-green-500', bg: 'bg-white', badge: 'bg-green-100 text-green-700' },
}

function RecommendationCard({ rec, index }) {
  const style = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.medium
  return (
    <div className={clsx('rounded-lg border-l-4 shadow-sm p-4', style.border, style.bg)}>
      <div className="flex items-center gap-2 mb-2">
        <span className={clsx('text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full', style.badge)}>
          {rec.priority}
        </span>
        {rec.category && (
          <span className="text-[10px] font-medium uppercase tracking-wider text-earth-400 px-2 py-0.5 rounded-full bg-earth-100">
            {rec.category}
          </span>
        )}
      </div>
      <p className="text-sm text-earth-800 leading-relaxed">{rec.text}</p>
    </div>
  )
}

// =============================================================================
// Generating Progress (animated multi-step)
// =============================================================================

const GENERATION_STEPS = [
  { id: 'gather', label: 'Gathering ranch data', icon: Database, duration: 4000 },
  { id: 'compute', label: 'Analysing metrics', icon: Target, duration: 3000 },
  { id: 'generate', label: 'Writing analysis', icon: Sparkles, duration: 18000 },
  { id: 'format', label: 'Formatting & finalizing', icon: FileTextIcon, duration: 3000 },
]

function GeneratingProgress() {
  const [activeStep, setActiveStep] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => setElapsed(e => e + 100), 100)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    let cumulative = 0
    for (let i = 0; i < GENERATION_STEPS.length; i++) {
      cumulative += GENERATION_STEPS[i].duration
      if (elapsed < cumulative) {
        setActiveStep(i)
        return
      }
    }
    setActiveStep(GENERATION_STEPS.length - 1)
  }, [elapsed])

  return (
    <div className="max-w-lg mx-auto py-16">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
          <Sparkles className="h-8 w-8 text-primary-600 animate-pulse" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Generating Your Report</h2>
        <p className="text-sm text-earth-500">This usually takes 1-2 minutes</p>
      </div>

      <div className="space-y-1">
        {GENERATION_STEPS.map((step, i) => {
          const isActive = i === activeStep
          const isComplete = i < activeStep
          const StepIcon = step.icon

          return (
            <div
              key={step.id}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-500',
                isActive && 'bg-primary-50 border border-primary-200',
                isComplete && 'opacity-60',
                !isActive && !isComplete && 'opacity-30'
              )}
            >
              <div className={clsx(
                'flex items-center justify-center w-8 h-8 rounded-full shrink-0 transition-colors',
                isComplete ? 'bg-green-100' : isActive ? 'bg-primary-100' : 'bg-earth-100'
              )}>
                {isComplete ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : isActive ? (
                  <Loader2 className="h-4 w-4 text-primary-600 animate-spin" />
                ) : (
                  <StepIcon className="h-4 w-4 text-earth-400" />
                )}
              </div>
              <span className={clsx(
                'text-sm font-medium',
                isActive ? 'text-primary-900' : isComplete ? 'text-earth-600' : 'text-earth-400'
              )}>
                {step.label}
              </span>
              {isComplete && (
                <span className="ml-auto text-xs text-green-600 font-medium">Done</span>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-8 h-1.5 bg-earth-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary-500 to-green-500 rounded-full transition-all duration-300"
          style={{
            width: `${Math.min(100, (elapsed / GENERATION_STEPS.reduce((s, step) => s + step.duration, 0)) * 100)}%`
          }}
        />
      </div>
    </div>
  )
}

// =============================================================================
// Regenerative Score Card (SVG gauge + component bars)
// =============================================================================

// Dynamic: show whichever components exist in the score object
const SCORE_COMPONENT_LABELS = {
  grazing_compliance: 'Grazing Compliance',
  ndvi_recovery: 'Vegetation Recovery',
  vegetation_health: 'Vegetation Health',
  invasive_control: 'Invasive Control',
  carbon_sequestration: 'Carbon Sequestration',
  // Legacy keys
  rotation_compliance: 'Rotation Compliance',
  stocking_density: 'Stocking Density',
}

function RegenerativeScoreCard({ score }) {
  if (!score) return null

  // Not yet scored — no components have measurable data (empty grazing
  // calendar, no vegetation monitoring). Show an honest placeholder instead
  // of a defaulted number.
  if (score.overall == null) {
    return (
      <div className="bg-white rounded-xl border border-earth-200 shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 rounded-lg bg-primary-100">
            <Target className="h-4 w-4 text-primary-600" />
          </div>
          <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Regenerative Progress</h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-earth-100 shrink-0">
            <span className="text-lg font-bold text-earth-400">—</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-earth-700">Not yet scored</p>
            <p className="text-sm text-earth-500 mt-0.5">
              {score.notes || 'Awaiting grazing calendar and vegetation monitoring data.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const overall = Math.round(score.overall)
  const circumference = 2 * Math.PI * 54
  const offset = circumference - (overall / 100) * circumference

  const getScoreColor = (val) => {
    if (val >= 75) return 'text-green-600'
    if (val >= 50) return 'text-amber-600'
    return 'text-red-600'
  }

  const getBarColor = (val) => {
    if (val >= 75) return 'bg-green-500'
    if (val >= 50) return 'bg-amber-500'
    return 'bg-red-500'
  }

  const getStrokeColor = (val) => {
    if (val >= 75) return '#16a34a'
    if (val >= 50) return '#d97706'
    return '#dc2626'
  }

  return (
    <div className="bg-white rounded-xl border border-earth-200 shadow-sm p-6 mb-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="p-1.5 rounded-lg bg-primary-100">
          <Target className="h-4 w-4 text-primary-600" />
        </div>
        <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Regenerative Progress</h2>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-8">
        <div className="relative shrink-0">
          <svg width="128" height="128" viewBox="0 0 128 128">
            <circle cx="64" cy="64" r="54" fill="none" stroke="#e5e7eb" strokeWidth="10" />
            <circle
              cx="64" cy="64" r="54"
              fill="none"
              stroke={getStrokeColor(overall)}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 64 64)"
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold ${getScoreColor(overall)}`}>{overall}</span>
            <span className="text-xs text-earth-400">/ 100</span>
          </div>
        </div>

        <div className="flex-1 w-full space-y-3">
          {Object.entries(SCORE_COMPONENT_LABELS)
            .filter(([key]) => score[key] != null)
            .map(([key, label]) => {
              const val = Math.round(score[key] ?? 0)
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-earth-700">{label}</span>
                    <span className={`text-sm font-semibold ${getScoreColor(val)}`}>{val}</span>
                  </div>
                  <div className="h-2 bg-earth-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${getBarColor(val)}`}
                      style={{ width: `${val}%` }}
                    />
                  </div>
                </div>
              )
            })}
          {score.notes && (
            <p className="text-xs text-earth-500 mt-2 italic">{score.notes}</p>
          )}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Section Component — renders a single report section with inline tables/charts
// =============================================================================

function ReportSection({ section, index, isDraft, editingSection, onStartEdit, onSaveEdit, onCancelEdit, editContent, setEditContent, updatePending }) {
  const sectionTables = section.tables || []
  const sectionCharts = section.chart_data || []

  return (
    <div
      id={`section-${section.section_id}`}
      className="bg-white rounded-xl border border-earth-200 shadow-sm p-6 mb-4 print:break-inside-avoid"
    >
      {/* Section header with ID badge — ecological sections get green tint */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {section.section_id && (
            <span className={clsx(
              'inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold shrink-0',
              ['ecological_overview', 'grazing', 'vegetation', 'invasive'].includes(section.section_type)
                ? 'bg-green-100 text-green-700'
                : ['dairy_production', 'dairy_reproductive', 'dairy_quality'].includes(section.section_type)
                ? 'bg-sky-100 text-sky-700'
                : 'bg-primary-100 text-primary-700'
            )}>
              {section.section_id}
            </span>
          )}
          <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
        </div>
        {isDraft && editingSection !== index && (
          <button
            onClick={() => onStartEdit(index)}
            className="inline-flex items-center gap-1 text-xs text-earth-500 hover:text-primary-600 print:hidden"
          >
            <Edit3 className="h-3.5 w-3.5" />
            Edit
          </button>
        )}
      </div>

      {editingSection === index ? (
        <div className="space-y-3">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            rows={12}
            className="w-full px-3 py-2 border border-earth-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={onCancelEdit}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-earth-600 hover:bg-earth-100 rounded-md"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
            <button
              onClick={onSaveEdit}
              disabled={updatePending}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Section markdown content */}
          <div className="prose prose-sm prose-earth max-w-none
            prose-headings:text-earth-900 prose-headings:font-semibold
            prose-p:text-earth-700 prose-p:leading-relaxed
            prose-strong:text-earth-900
            prose-li:text-earth-700 prose-li:leading-relaxed
            prose-ul:my-2 prose-ol:my-2
          ">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content_markdown}</ReactMarkdown>
          </div>

          {/* Inline tables */}
          {sectionTables.map((table, ti) => (
            <ReportTable key={ti} table={table} />
          ))}

          {/* Inline charts */}
          {sectionCharts.length > 0 && (
            <div className={clsx(
              'mt-4',
              sectionCharts.length > 1 ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : ''
            )}>
              {sectionCharts.map((chart, ci) => (
                <ReportChart key={ci} chart={chart} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// =============================================================================
// Main ReportViewer
// =============================================================================

export default function ReportViewer({ ranchId }) {
  const { reportId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editingSection, setEditingSection] = useState(null)
  const [editContent, setEditContent] = useState('')

  const { data: report, isLoading } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'generating' ? 3000 : false
    },
  })

  const updateSectionMutation = useMutation({
    mutationFn: ({ index, content }) => updateReportSection(reportId, index, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] })
      setEditingSection(null)
    },
  })

  const approveMutation = useMutation({
    mutationFn: () => approveReport(reportId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['report', reportId] }),
  })

  const archiveMutation = useMutation({
    mutationFn: () => archiveReport(reportId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['report', reportId] }),
  })

  const regenerateMutation = useMutation({
    mutationFn: () => regenerateReport(reportId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['report', reportId] }),
  })

  const startEditing = (index) => {
    setEditingSection(index)
    setEditContent(report.sections[index].content_markdown)
  }

  const saveEdit = () => {
    updateSectionMutation.mutate({ index: editingSection, content: editContent })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!report) {
    return (
      <div className="text-center py-20">
        <p className="text-earth-500">Report not found</p>
      </div>
    )
  }

  if (report.status === 'generating') {
    return <GeneratingProgress />
  }

  if (report.status === 'error') {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-6">
          <AlertTriangle className="h-10 w-10 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Generation Failed</h2>
        <p className="text-earth-500 mb-4">{report.executive_summary || 'An unknown error occurred'}</p>
        <div className="flex justify-center gap-3">
          <button onClick={() => navigate('/reports')} className="px-4 py-2 text-sm font-medium text-earth-700 hover:bg-earth-100 rounded-lg">
            Back to Reports
          </button>
          <button
            onClick={() => regenerateMutation.mutate()}
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 font-medium"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  const sections = report.sections || []
  const charts = report.chart_data || []
  const recommendations = report.recommendations || []
  const kpiTiles = report.kpi_tiles || []
  const anomalyAlerts = report.anomaly_alerts || []
  const isDraft = report.status === 'draft'

  // Charts not assigned to any section go into a general section
  const assignedSectionIds = new Set(sections.map(s => s.section_id).filter(Boolean))
  const orphanCharts = charts.filter(c => !c.section_ref || !assignedSectionIds.has(c.section_ref))

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate('/reports')}
        className="inline-flex items-center gap-1 text-sm text-earth-500 hover:text-earth-700 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Reports
      </button>

      {/* Report Header */}
      <div className="bg-white rounded-xl border border-earth-200 shadow-sm p-6 mb-6 print:border-none print:shadow-none">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{report.title}</h1>
            <p className="text-earth-500 mt-1">
              {new Date(report.period_start).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
              {' — '}
              {new Date(report.period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              {report.report_number && (
                <span className="ml-2 text-earth-400">Report #{report.report_number}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            {isDraft && (
              <button
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
                className="inline-flex items-center gap-1.5 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm font-medium"
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </button>
            )}
            <button
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
              className="inline-flex items-center gap-1.5 border border-earth-300 px-3 py-2 rounded-lg hover:bg-earth-50 text-sm font-medium text-earth-700"
            >
              <RefreshCw className={`h-4 w-4 ${regenerateMutation.isPending ? 'animate-spin' : ''}`} />
              Regenerate
            </button>
            {report.status !== 'archived' && (
              <button
                onClick={() => archiveMutation.mutate()}
                className="inline-flex items-center gap-1.5 border border-earth-300 px-3 py-2 rounded-lg hover:bg-earth-50 text-sm font-medium text-earth-700"
              >
                <Archive className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 border border-earth-300 px-3 py-2 rounded-lg hover:bg-earth-50 text-sm font-medium text-earth-700"
            >
              <Printer className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Status & meta bar */}
        <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-earth-100 text-xs text-earth-400">
          <span className={clsx(
            'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold',
            report.status === 'published' ? 'bg-green-100 text-green-800' :
            report.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-600'
          )}>
            {report.status}
          </span>
          {report.generation_time_seconds && <span>Generated in {report.generation_time_seconds}s</span>}
        </div>
      </div>

      {/* KPI Traffic-Light Tiles */}
      <KPITileRow tiles={kpiTiles} />

      {/* Anomaly Alerts */}
      <AnomalyAlertBox alerts={anomalyAlerts} />

      {/* Executive Summary */}
      {report.executive_summary && (
        <div className="bg-gradient-to-br from-primary-50 to-green-50 rounded-xl border border-primary-200 shadow-sm p-6 mb-6">
          <h2 className="text-base font-bold text-primary-900 mb-3 uppercase tracking-wide">Executive Summary</h2>
          <div className="prose prose-sm max-w-none text-earth-800 prose-p:leading-relaxed prose-strong:text-primary-900">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.executive_summary}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Regenerative Score */}
      {report.regenerative_score && (
        <RegenerativeScoreCard score={report.regenerative_score} />
      )}

      {/* Report Sections — ordered by section.order, with inline tables + charts */}
      {[...sections]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((section, index) => {
          const originalIndex = sections.indexOf(section)
          return (
            <ReportSection
              key={section.section_id || index}
              section={section}
              index={originalIndex}
              isDraft={isDraft}
              editingSection={editingSection}
              onStartEdit={startEditing}
              onSaveEdit={saveEdit}
              onCancelEdit={() => setEditingSection(null)}
              editContent={editContent}
              setEditContent={setEditContent}
              updatePending={updateSectionMutation.isPending}
            />
          )
        })}

      {/* Orphan charts (not attached to any section) */}
      {orphanCharts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 print:grid-cols-2">
          {orphanCharts.map((chart, i) => (
            <ReportChart key={i} chart={chart} />
          ))}
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-white rounded-xl border border-earth-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-primary-100">
              <ClipboardList className="h-4 w-4 text-primary-600" />
            </div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Recommendations</h2>
          </div>
          <div className="space-y-3">
            {recommendations.map((rec, i) => (
              <RecommendationCard key={i} rec={rec} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          /* Reset page */
          body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

          /* Hide app chrome */
          .lg\\:fixed, .sticky, nav, header { display: none !important; }
          .lg\\:pl-64 { padding-left: 0 !important; }
          main { padding: 0 !important; }
          .px-4, .sm\\:px-6, .lg\\:px-8 { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }

          /* Report-specific */
          .print\\:hidden { display: none !important; }
          .print\\:border-none { border: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          .print\\:grid-cols-2 { grid-template-columns: repeat(2, 1fr); }

          /* Page breaks */
          h2 { page-break-after: avoid; }
          .card, .rounded-xl { break-inside: avoid; }

          /* Typography */
          * { font-size: 11pt; }
          h1 { font-size: 18pt !important; }
          h2 { font-size: 14pt !important; }
          h3 { font-size: 12pt !important; }

          /* Charts */
          .recharts-wrapper { max-height: 250px !important; }
        }
      `}</style>
    </div>
  )
}
